import React, { useState, useEffect } from 'react';
import { X, Calendar, FileText, Clock, CheckCircle } from 'lucide-react';
import { timelineAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface TimelineEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeline: {
    id: number;
    title: string;
    description?: string;
    due_date?: string;
    status: 'pending' | 'in_progress' | 'completed' | 'overdue';
    publication_title?: string;
    publication_id?: number;
    publication_stage?: string;
  };
  onTimelineUpdated: () => void;
}

const TimelineEditModal: React.FC<TimelineEditModalProps> = ({
  isOpen,
  onClose,
  timeline,
  onTimelineUpdated
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState<'pending' | 'in_progress' | 'completed' | 'overdue'>('pending');
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && timeline) {
      setTitle(timeline.title || '');
      setDescription(timeline.description || '');
      
      // Format the due_date properly for the date input (YYYY-MM-DD format)
      let formattedDueDate = '';
      if (timeline.due_date) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(timeline.due_date)) {
          // Already in YYYY-MM-DD format
          formattedDueDate = timeline.due_date;
        } else {
          // Convert from datetime string to date
          const date = new Date(timeline.due_date);
          if (!isNaN(date.getTime())) {
            formattedDueDate = date.toISOString().split('T')[0];
          }
        }
      }
      setDueDate(formattedDueDate);
      
      setStatus(timeline.status || 'pending');
    }
  }, [isOpen, timeline]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Timeline title is required');
      return;
    }

    setIsLoading(true);
    try {
      // Handle due_date properly - if empty, send null; if has value, ensure it's in YYYY-MM-DD format
      let finalDueDate = null;
      if (dueDate && dueDate.trim()) {
        // If it's already in YYYY-MM-DD format, use it as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dueDate.trim())) {
          finalDueDate = dueDate.trim();
        } else {
          // Convert to YYYY-MM-DD format
          const date = new Date(dueDate);
          if (!isNaN(date.getTime())) {
            finalDueDate = date.toISOString().split('T')[0];
          }
        }
      }

      await timelineAPI.updateTimelineItem(timeline.id, {
        title: title.trim(),
        description: description.trim() || null,
        due_date: finalDueDate,
        status: status
      });
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      if (timeline.publication_id) {
        cacheService.clearPattern(`/publications/${timeline.publication_id}/batch`);
      }
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Timeline item updated successfully');
      onTimelineUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating timeline item:', error);
      toast.error('Failed to update timeline item');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'in_progress': return <Clock className="h-4 w-4" />;
      case 'overdue': return <Clock className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Timeline Item
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {timeline.publication_title && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Publication
              </h3>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                {timeline.publication_title}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timeline Title *
              </label>
              {(timeline.publication_stage === '3' || timeline.publication_stage === '4') ? (
                <select
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="">Select a title...</option>
                  <option value="Main messages, figures, and tables">Main messages, figures, and tables</option>
                  <option value="Draft completed">Draft completed</option>
                  <option value="Internal review">Internal review</option>
                  <option value="External review">External review</option>
                  <option value="Submission">Submission</option>
                  <option value="Revision">Revision</option>
                </select>
              ) : (
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="Enter timeline title"
                    required
                  />
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter timeline description (optional)"
              />
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date <span className="text-gray-500 text-sm">(optional)</span>
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Select a due date (optional)"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Status
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-800' },
                  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
                  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
                  { value: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-800' }
                ].map((statusOption) => (
                  <label key={statusOption.value} className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      value={statusOption.value}
                      checked={status === statusOption.value}
                      onChange={(e) => setStatus(e.target.value as any)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {statusOption.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Current Status Preview */}
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
              <div className="flex items-center space-x-3">
                <span className="font-medium text-gray-900">{title || 'Timeline Title'}</span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                  {getStatusIcon(status)}
                  <span className="ml-1">{status.replace('_', ' ')}</span>
                </span>
                {dueDate && dueDate.trim() ? (
                  <span className="text-xs text-gray-500">
                    Due: {(() => {
                      try {
                        return new Date(dueDate).toLocaleDateString();
                      } catch (error) {
                        return dueDate; // Fallback to raw value if date parsing fails
                      }
                    })()}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">
                    No due date set
                  </span>
                )}
              </div>
              {description && (
                <p className="text-sm text-gray-600 mt-2">{description}</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Updating...' : 'Update Timeline'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TimelineEditModal;
